import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  X,
  Briefcase,
  Buildings,
  UsersFour,
  Spinner,
  ShieldCheck,
  Scales,
  MagnifyingGlass,
  CaretDown,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/features/auth/store';
import { createThread, type MessageThread, type ThreadKind, type PrivilegeMarker } from '@/lib/messagingApi';
import { fetchUsers, type ApiUser } from '@/lib/usersApi';
import { fetchEligibleParticipants, resolveRecipient } from '@/lib/communicationsApi';
import { fetchMatters } from '@/lib/mattersApi';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useChambersMode } from '@/lib/useChambersMode';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Matter {
  id: string;
  caseNumber: string;
  caseName: string;
}
// Note: Matter is derived from mattersApi.Matter via rawMatters mapping below

interface Props {
  matterId?: string;
  defaultKind: 'matter_internal' | 'chambers_internal' | 'matter_external';
  onClose: () => void;
  onCreated: (thread: MessageThread) => void;
}

const ROLE_DISPLAY: Record<string, string> = {
  admin: 'Admin',
  senior_clerk: 'Senior Clerk',
  junior_clerk: 'Junior Clerk',
  silk: 'Silk',
  junior: 'Junior',
  pupil: 'Pupil',
  door_tenant: 'Door Tenant',
  solicitor: 'Solicitor',
  staff: 'Staff',
};

export function NewThreadModal({ matterId, defaultKind, onClose, onCreated }: Props) {
  const currentUser = useAuthStore((s) => s.user);
  // `isSolo` is the server's own answer, delivered on the session as `chambers.workspaceType`,
  // so it is correct on first render and never pending (useChambersMode.ts).
  //
  // This used to read `isSolo && !loading`, guarding against an earlier version of the hook that
  // DERIVED solo by counting barrister rows in a client store ("count 0 → solo until loaded"),
  // which wrongly read solo for a clerk or for a barrister who opened this modal before the
  // store filled. That derivation is gone and `loading` is now permanently false, so the guard
  // had no effect — it only described a hazard that no longer exists.
  const { isSolo: treatAsSolo } = useChambersMode();
  const [kind, setKind] = useState<ThreadKind>(
    matterId ? (defaultKind === 'matter_external' ? 'matter_external' : 'matter_internal') : defaultKind,
  );

  // Solo barristers have no colleagues — chambers_internal is always empty; redirect them
  useEffect(() => {
    if (treatAsSolo && kind === 'chambers_internal') setKind(matterId ? 'matter_internal' : 'matter_external');
  }, [treatAsSolo, kind, matterId]);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(matterId ?? null);
  const [subject, setSubject] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<ApiUser[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState('');
  const [privilege, setPrivilege] = useState<PrivilegeMarker>('none');

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch all chamber users (correct response shape: res.data.data)
  const { data: allUsers = [], isLoading: usersLoading } = useQuery({
    queryKey: ['chambers-users-modal', 'comms-capable'],
    // Exclude staff who cannot participate in communications (e.g. a junior clerk without the
    // `emails` capability) — otherwise they could be added but then get 403 opening the thread.
    queryFn: () => fetchUsers({ limit: 200, status: 'active', communicationsCapable: true }),
    staleTime: 0,
  });

  // Fetch solicitors separately for matter_external threads
  const { data: solicitorUsers = [], isLoading: solicitorsLoading } = useQuery({
    queryKey: ['solicitor-users-modal', kind],
    queryFn: () => fetchUsers({ memberType: 'solicitor', limit: 100 }),
    enabled: kind === 'matter_external',
    staleTime: 0,
  });

  // Fetch matters — fetchMatters() returns { matters, truncated }; extract the array
  const { data: rawMatters = [], isLoading: mattersLoading } = useQuery({
    queryKey: ['matters-picker-thread'],
    queryFn: () => fetchMatters().then((r) => r.matters),
    enabled: (kind === 'matter_internal' || kind === 'matter_external') && !matterId,
    staleTime: 0,
  });
  // Normalise to the shape the modal uses
  const matters: Matter[] = rawMatters.map((m) => ({
    id: m.id,
    caseNumber: m.number,
    caseName: m.name,
  }));

  // Who may legitimately join a conversation about THIS matter (4C-1 / D3). Resolved by the
  // server from the same matter-access engine that enforces thread-create and participant-add, so
  // the picker cannot offer someone the server would refuse. The browser keeps no permission
  // model of its own — with no matter chosen there is no matter to be eligible for, and the
  // chambers-wide list applies.
  const isMatterThread = kind === 'matter_internal' || kind === 'matter_external';
  const { data: eligibleIds, isLoading: eligibleLoading } = useQuery({
    queryKey: ['matter-eligible-participants', selectedMatterId],
    queryFn: () => fetchEligibleParticipants(selectedMatterId!).then(u => new Set(u.map(x => x.id))),
    enabled: Boolean(selectedMatterId) && isMatterThread,
    staleTime: 0,
  });

  // Combined selectable pool
  const pool = useMemo(() => {
    const base = kind === 'matter_external'
      ? [...allUsers, ...solicitorUsers.filter(s => !allUsers.some(u => u.id === s.id))]
      : allUsers;
    const solicitorIds = new Set(solicitorUsers.map(s => s.id));
    return base.filter(u =>
      u.id !== currentUser?.id &&
      !selectedUsers.some(s => s.id === u.id) &&
      // On a matter thread, drop anyone without access to that matter. Solicitors on an external
      // thread are authorised by their instructing-firm link, not by matter access, so they stay.
      (!selectedMatterId || !isMatterThread || !eligibleIds
        ? true
        : eligibleIds.has(u.id) || (kind === 'matter_external' && solicitorIds.has(u.id))),
    );
  }, [allUsers, solicitorUsers, kind, currentUser, selectedUsers, selectedMatterId, isMatterThread, eligibleIds]);

  // Filtered by search query
  const filtered = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(u => {
      const hay = `${u.firstName} ${u.lastName} ${u.email} ${u.memberType} ${u.phone ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pool, userQuery]);

/**
   * Addressing someone by EMAIL (4C-1 D2, surfaced here in 4C-5).
   *
   * The list above only filters people already loaded into the picker. A barrister who knows a
   * colleague only as an email address had no way through: the box said "Search by name, email or
   * role" and then answered "No users match". The server can answer that question safely, so we
   * ask it — but only when the query IS an address and the local list has nothing, so ordinary
   * typing never becomes a lookup.
   *
   * The three outcomes come straight from the server and are shown as they are. `unavailable`
   * deliberately reads the same whether nobody holds that address or the caller may not address
   * them — that indistinguishability is the contract, and the UI must not undo it by guessing.
   */
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userQuery.trim());
  const shouldResolve = looksLikeEmail && filtered.length === 0;
  const {
    data: resolution,
    isFetching: resolving,
    error: resolveError,
  } = useQuery({
    queryKey: ['resolve-recipient', userQuery.trim().toLowerCase(), selectedMatterId],
    queryFn: () => resolveRecipient(userQuery.trim().toLowerCase(), isMatterThread ? selectedMatterId : null),
    enabled: shouldResolve && dropdownOpen,
    retry: false,
    staleTime: 30_000,
  });

  /** A resolved person is a real, addressable member — turn them into a selectable row. */
  const resolvedUser: ApiUser | null =
    resolution?.outcome === 'resolved'
      ? ({
          id: resolution.user.userId,
          email: userQuery.trim().toLowerCase(),
          firstName: resolution.user.displayName.split(' ')[0] ?? resolution.user.displayName,
          lastName: resolution.user.displayName.split(' ').slice(1).join(' '),
          memberType: resolution.user.memberType,
        } as ApiUser)
      : null;

  const resolveMessage = (): string | null => {
    if (!shouldResolve) return null;
    if (resolving) return 'Checking that address…';
    if (resolveError) {
      const status = (resolveError as { response?: { status?: number } })?.response?.status;
      if (status === 429) return 'Too many lookups just now — wait a moment and try again.';
      return 'That address could not be checked right now.';
    }
    if (resolution?.outcome === 'invitable') {
      return 'Nobody here uses that address yet. They can be invited to the workspace from People & Access.';
    }
    if (resolution?.outcome === 'unavailable') {
      return 'No one you can add matches that address.';
    }
    return null;
  };

  const handleSelectUser = (user: ApiUser) => {
    setSelectedUsers(prev => [...prev, user]);
    setUserQuery('');
    inputRef.current?.focus();
  };

  const handleRemoveUser = (id: string) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== id));
  };

  // Create thread mutation
  const createMutation = useMutation({
    mutationFn: () =>
      createThread({
        kind,
        matterId: (kind === 'matter_internal' || kind === 'matter_external')
          ? selectedMatterId ?? undefined
          : undefined,
        subject: subject.trim() || undefined,
        participantUserIds: selectedUsers.map(u => u.id),
        initialMessage: initialMessage.trim() || undefined,
        privilege: privilege !== 'none' ? privilege : undefined,
      }),
    onSuccess: (thread) => {
      toast.success('Conversation started');
      onCreated(thread);
    },
    onError: (err: unknown) => {
      // The server's 4xx messages here are written for the person reading them (for example
      // "One or more people do not have access to this matter"), and swallowing them leaves the
      // user with a dead end. Anything else stays generic — a 500 body is not for the browser.
      const res = (err as { response?: { status?: number; data?: { error?: { message?: string } } } })?.response;
      const serverMessage = res?.data?.error?.message;
      const actionable = res?.status && res.status >= 400 && res.status < 500 && serverMessage;
      toast.error(actionable ? serverMessage : 'Could not create conversation');
    },
  });

  const subjectRequired = kind === 'matter_internal' || kind === 'matter_external';
  const canSubmit =
    selectedUsers.length > 0 &&
    !createMutation.isPending &&
    (kind === 'chambers_internal' || Boolean(selectedMatterId)) &&
    (!subjectRequired || subject.trim().length > 0);

  const isLoadingUsers = usersLoading || (kind === 'matter_external' && solicitorsLoading) || eligibleLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <Card className="my-auto flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="text-sm font-semibold tracking-tight text-foreground">New conversation</div>
            <div className="text-xs text-muted-foreground">
              Start a new conversation with chambers staff or external parties
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">

          {/* Conversation type */}
          {!matterId && (
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Conversation type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { key: 'chambers_internal', Icon: Buildings, title: 'Chambers', sub: 'Internal staff only' },
                  { key: 'matter_internal',   Icon: Briefcase, title: 'Case (internal)', sub: 'Staff on a matter' },
                  { key: 'matter_external',   Icon: UsersFour, title: 'With solicitor', sub: 'Include external firm' },
                ] as const).filter(({ key }) => !(treatAsSolo && key === 'chambers_internal')).map(({ key, Icon, title, sub }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setKind(key)}
                    className={`flex flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                      kind === key
                        ? 'border-brand-600 bg-brand-50/50 dark:bg-brand-900/30'
                        : 'border-border hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                      <Icon size={13} weight="duotone" className={kind === key ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'} />
                      {title}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{sub}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Matter selector */}
          {(kind === 'matter_internal' || kind === 'matter_external') && !matterId && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Matter <span className="text-red-500">*</span>
              </label>
              {mattersLoading ? (
                <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                  <Spinner size={14} className="animate-spin" /> Loading matters…
                </div>
              ) : (
                <SearchableSelect
                  value={selectedMatterId ?? ''}
                  onChange={(v) => setSelectedMatterId(v || null)}
                  placeholder="Select a matter…"
                  options={matters.map((m) => ({
                    value: m.id,
                    label: `${m.caseNumber} — ${m.caseName}`,
                  }))}
                />
              )}
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Subject {subjectRequired ? <span className="text-red-500">*</span> : <span className="font-normal normal-case">(optional)</span>}
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={subjectRequired ? 'e.g. Conference prep — trial dates' : 'e.g. Friday court coverage'}
              maxLength={200}
              className={subjectRequired && !subject.trim() ? 'border-amber-400 focus:ring-amber-400' : ''}
            />
            {subjectRequired && !subject.trim() && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Required — matter threads must have a subject</p>
            )}
          </div>

          {/* Privilege marker */}
          {(kind === 'matter_internal' || kind === 'matter_external') && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Privilege marker
              </label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { key: 'none',              label: 'None',            cls: '' },
                  { key: 'legal_advice',      label: 'Legal advice',    cls: 'text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-500/40 bg-blue-50/50 dark:bg-blue-500/10' },
                  { key: 'without_prejudice', label: 'W/O prejudice',   cls: 'text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/40 bg-amber-50/50 dark:bg-amber-500/10' },
                ] as const).map(({ key, label, cls }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPrivilege(key)}
                    className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium transition-colors ${
                      privilege === key ? cls || 'border-brand-600 bg-brand-50/50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400' : 'border-border text-muted-foreground hover:bg-muted/40'
                    }`}
                  >
                    {key === 'legal_advice' && <ShieldCheck size={11} weight="duotone" />}
                    {key === 'without_prejudice' && <Scales size={11} weight="duotone" />}
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Participants — searchable dropdown with chips */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Participants <span className="font-normal normal-case text-muted-foreground/70">({selectedUsers.length} selected)</span>
            </label>

            {/* Selected user chips */}
            {selectedUsers.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {selectedUsers.map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex items-center gap-1 rounded-full border border-brand-200 dark:border-brand-500/30 bg-brand-50 dark:bg-brand-900/20 py-0.5 pl-2.5 pr-1 text-xs font-medium text-brand-800 dark:text-brand-300"
                  >
                    {u.firstName} {u.lastName}
                    <span className="ml-0.5 rounded-full text-[9px] uppercase tracking-wide text-brand-500">
                      {ROLE_DISPLAY[u.memberType] ?? u.memberType}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(u.id)}
                      className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-brand-400 dark:text-brand-500 transition hover:bg-brand-200 dark:hover:bg-brand-900/30 hover:text-brand-700 dark:hover:text-brand-400"
                    >
                      <X size={9} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search input + dropdown */}
            <div ref={dropdownRef} className="relative">
              <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 focus-within:ring-1 focus-within:ring-brand-600">
                <MagnifyingGlass size={14} className="shrink-0 text-muted-foreground" />
                <input
                  ref={inputRef}
                  type="text"
                  value={userQuery}
                  onChange={(e) => { setUserQuery(e.target.value); setDropdownOpen(true); }}
                  onFocus={() => setDropdownOpen(true)}
                  placeholder="Search by name, email or role…"
                  className="flex-1 bg-transparent text-sm placeholder:text-muted-foreground/60 focus:outline-none"
                />
                {isLoadingUsers ? (
                  <Spinner size={13} className="animate-spin text-muted-foreground" />
                ) : (
                  <CaretDown
                    size={13}
                    className={`shrink-0 text-muted-foreground transition ${dropdownOpen ? 'rotate-180' : ''}`}
                  />
                )}
              </div>

              {/* Dropdown list */}
              {dropdownOpen && !isLoadingUsers && (
                <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-52 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
                  {resolvedUser ? (
                    /* The server confirmed this address belongs to someone the caller may address. */
                    <button
                      type="button"
                      onClick={() => handleSelectUser(resolvedUser)}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm transition hover:bg-muted"
                    >
                      <span className="font-medium text-foreground">
                        {resolution?.outcome === 'resolved' ? resolution.user.displayName : ''}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {resolution?.outcome === 'resolved' ? resolution.user.memberType.replace(/_/g, ' ') : ''}
                      </span>
                    </button>
                  ) : resolveMessage() ? (
                    <div className="px-4 py-5 text-center text-xs text-muted-foreground">
                      {resolveMessage()}
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="px-4 py-5 text-center text-xs text-muted-foreground">
                      {userQuery.trim()
                        ? `No users match "${userQuery}"`
                        : kind === 'matter_external'
                          ? 'No solicitors with portal access found. Invite a solicitor from their contact record to grant portal access.'
                          : treatAsSolo
                            ? 'No other members in this chambers.'
                            : 'All available members selected or no members found'}
                    </div>
                  ) : (
                    filtered.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); handleSelectUser(u); }}
                        className="flex w-full items-center gap-3 border-b border-border/60 px-3 py-2.5 text-left transition last:border-b-0 hover:bg-brand-50/60 dark:hover:bg-brand-900/20"
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30 text-[10px] font-bold text-brand-700 dark:text-brand-400">
                          {u.firstName[0]}{u.lastName[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground">
                            {u.firstName} {u.lastName}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {u.email}{u.phone ? ` · ${u.phone}` : ''}
                          </div>
                        </div>
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          {ROLE_DISPLAY[u.memberType] ?? u.memberType}
                        </Badge>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* First message */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              First message <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
            </label>
            <textarea
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              placeholder="Type your opening message…"
              maxLength={4000}
              rows={3}
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-600"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border bg-muted/20 px-5 py-3">
          <span className="text-xs text-muted-foreground">
            {selectedUsers.length > 0
              ? `${selectedUsers.length} participant${selectedUsers.length > 1 ? 's' : ''} selected`
              : 'Select at least one participant'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={() => createMutation.mutate()} disabled={!canSubmit}>
              {createMutation.isPending ? (
                <><Spinner size={13} className="mr-1.5 animate-spin" />Creating…</>
              ) : (
                'Start conversation'
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
